import type { MyOAuthConnectionsQuery } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { toaster } from '#src/components/ui/toaster';
import { Tooltip } from '#src/components/ui/tooltip';
import { DELETE_OAUTH_CONNECTION } from '#src/graphql/mutations/delete-oauth-connection';
import { TEST_GMAIL_CONNECTION } from '#src/graphql/mutations/test-gmail-connection';
import { UPDATE_OAUTH_CONNECTION } from '#src/graphql/mutations/update-oauth-connection';
import { MY_OAUTH_CONNECTIONS } from '#src/graphql/queries/my-oauth-connections';
import { useAppConfig } from '#src/providers/app-config-provider';

type ConnectionRow = MyOAuthConnectionsQuery['myOAuthConnections'][number];

function oauthErrorMessage(code: string): string {
  const map: Record<string, string> = {
    gmail_denied: 'Google sign-in was cancelled.',
    gmail_not_configured: 'Gmail linking is not configured on the server.',
    gmail_provider_error: 'Google returned an error during sign-in.',
    gmail_invalid_request: 'The OAuth request was incomplete.',
    gmail_missing_refresh:
      'Google did not return a refresh token. Try revoking app access in your Google account and connect again.',
    gmail_invalid_profile: 'Could not read your Google profile.',
    gmail_callback_failed: 'Could not finish linking Gmail. Try again.',
    gmail_invalid_state: 'The sign-in session expired. Try connecting again.',
  };
  return map[code] ?? 'Something went wrong while connecting.';
}

function GmailConnectionCard({
  connection,
  onRemoved,
}: {
  connection: ConnectionRow;
  onRemoved: () => void;
}) {
  const [localDescription, setLocalDescription] = useState(
    connection.description,
  );
  useEffect(() => {
    setLocalDescription(connection.description);
  }, [connection.description, connection.id]);

  const [updateOAuthConnection, { loading: saving }] = useMutation(
    UPDATE_OAUTH_CONNECTION,
  );
  const [deleteOAuthConnection, { loading: deleting }] = useMutation(
    DELETE_OAUTH_CONNECTION,
  );
  const [testGmailConnection, { loading: testing }] = useMutation(
    TEST_GMAIL_CONNECTION,
  );

  const trimmedLocal = localDescription.trim();
  const trimmedServer = connection.description.trim();
  const saveDisabled = trimmedLocal === trimmedServer;

  const metadata = connection.metadata as
    | { email?: string | null; name?: string | null }
    | null
    | undefined;
  const hint =
    typeof metadata?.email === 'string' && metadata.email.length > 0
      ? metadata.email
      : null;

  const save = async () => {
    try {
      await updateOAuthConnection({
        variables: {
          input: { id: connection.id, description: trimmedLocal },
        },
      });
      toaster.create({
        title: 'Saved',
        description: 'Connection description updated.',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not save description.',
        type: 'error',
      });
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        'Remove this Gmail connection? You can connect again later.',
      )
    ) {
      return;
    }
    try {
      const res = await deleteOAuthConnection({
        variables: { id: connection.id },
      });
      if (!res.data?.deleteOAuthConnection) {
        toaster.create({
          title: 'Not removed',
          description: 'Connection was not found or already removed.',
          type: 'warning',
        });
        return;
      }
      toaster.create({
        title: 'Removed',
        description: 'Gmail connection removed.',
        type: 'success',
        duration: 2500,
        closable: true,
      });
      onRemoved();
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not remove connection.',
        type: 'error',
      });
    }
  };

  const testConnection = async () => {
    try {
      const res = await testGmailConnection({
        variables: { connectionId: connection.id },
      });
      const payload = res.data?.testGmailConnection;
      if (!payload) {
        toaster.create({
          title: 'Error',
          description: 'No response from server.',
          type: 'error',
        });
        return;
      }
      if (payload.success) {
        const preview =
          payload.subjects.length > 0
            ? payload.subjects.slice(0, 3).join(' · ')
            : 'Inbox sample is empty (no recent messages matched).';
        toaster.create({
          title: 'Gmail test OK',
          description: `${payload.fetchedCount} message(s): ${preview}${payload.subjects.length > 3 ? ' …' : ''}`,
          type: 'success',
          duration: 5000,
          closable: true,
        });
        return;
      }
      toaster.create({
        title: 'Gmail test failed',
        description: payload.errorMessage ?? 'Unknown error.',
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not reach Gmail.',
        type: 'error',
      });
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      p={4}
      bg="bg.subtle"
    >
      <VStack gap={3} align="stretch">
        <HStack justify="space-between" align="flex-start" flexWrap="wrap">
          <VStack align="start" gap={1}>
            <HStack gap={2}>
              <Badge colorPalette="blue" size="sm">
                {connection.provider}
              </Badge>
              {hint ? (
                <Text fontSize="xs" color="fg.muted">
                  {hint}
                </Text>
              ) : null}
            </HStack>
            <Text fontSize="xs" color="fg.muted">
              Account id: {connection.providerAccountId}
            </Text>
          </VStack>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              loading={testing}
              disabled={deleting}
              onClick={() => void testConnection()}
            >
              Test
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              loading={deleting}
              disabled={testing}
              onClick={() => void remove()}
            >
              Remove
            </Button>
          </HStack>
        </HStack>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            Description (for you and future AI features)
          </Text>
          <ChatComposerRow
            value={localDescription}
            onChange={setLocalDescription}
            onSubmit={() => void save()}
            placeholder="Label this connection…"
            submitOnEnter={false}
            autoGrow
            disabled={saving}
            loading={saving}
            submitDisabled={saveDisabled}
            submitAriaLabel="Save description"
            submitTitle="Save description"
            submitIcon={<FiCheck size={18} strokeWidth={2.5} aria-hidden />}
          />
        </Box>
      </VStack>
    </Box>
  );
}

export function SettingsIntegrationsSection() {
  const { config } = useAppConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, refetch } = useQuery(MY_OAUTH_CONNECTIONS);

  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const oauthError = searchParams.get('oauth_error');
    if (!oauth && !oauthError) return;

    const next = new URLSearchParams(searchParams);
    next.delete('oauth');
    next.delete('oauth_error');
    setSearchParams(next, { replace: true });

    if (oauth === 'gmail_success') {
      void refetch();
      toaster.create({
        title: 'Gmail connected',
        description: 'You can add a description below to label this account.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
      return;
    }

    if (oauthError) {
      toaster.create({
        title: 'Could not connect Gmail',
        description: oauthErrorMessage(oauthError),
        type: 'error',
        duration: 6000,
        closable: true,
      });
    }
  }, [searchParams, setSearchParams, refetch]);

  const rows = data?.myOAuthConnections ?? [];
  const authorizeUrl = `${config.apiHost}/api/oauth/gmail/authorize`;

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
        Integrations
      </Text>
      <Text fontSize="xs" color="fg.muted" mb={3}>
        Connect external accounts. Labels help you tell connections apart.
      </Text>

      <HStack gap={2} flexWrap="wrap" mb={4}>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => {
            window.location.href = authorizeUrl;
          }}
        >
          Connect Gmail
        </Button>
        <Tooltip content="Coming soon" showArrow>
          <Box as="span" display="inline-block">
            <Button size="sm" variant="outline" disabled>
              Connect Trello
            </Button>
          </Box>
        </Tooltip>
      </HStack>

      {loading && !data ? (
        <Text fontSize="sm" color="fg.muted">
          Loading connections…
        </Text>
      ) : null}
      {error ? (
        <Text fontSize="sm" color="red.500">
          {error.message}
        </Text>
      ) : null}

      {!loading && rows.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          No accounts linked yet. Use Connect Gmail above to add one.
        </Text>
      ) : null}

      {rows.length > 0 ? (
        <VStack gap={3} align="stretch">
          {rows.map((c) => (
            <GmailConnectionCard
              key={c.id}
              connection={c}
              onRemoved={() => void refetch()}
            />
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}
